import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtError } from "../lib/api";
import { toast } from "sonner";
import FadeIn from "../components/FadeIn";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Trash, Plus, PencilSimple, ArrowLeft, QrCode, Receipt, CheckCircle, Clock, Prohibit, Sparkle, WarningCircle, CaretDown, MagnifyingGlass, MapPin, UploadSimple, Download, X } from "@phosphor-icons/react";
import { CsDashboard } from "../components/dashboard/CsDashboard";
import { AiInsightsFeed, ScansResponsesChart } from "./BusinessDashboard";
import { PMAssignmentWidget } from "./AdminExtras";
import { AdminDocumentsPanel } from "../components/documents/AdminDocumentsPanel";
import { formatDate, formatRangeInclusive } from "../lib/date";

// ============================================================================
// Businesses — list + create + edit + delete
// ============================================================================
// Every state-changing action on a business (Suspend/Lift, Lock/Unlock,
// Delete) is confirmed via one shared dialog fed by `actionTarget` below —
// each entry says exactly what that action does before the admin commits.
const ACTION_META = {
  suspend: {
    title: (b) => `Suspend "${b.business_name}"?`,
    body: "This pauses their public QR listing — customers scanning the QR code will see a \"temporarily paused\" message instead of the rating form. The business can still log into their dashboard (e.g. to see and pay an overdue invoice). You can lift this instantly at any time.",
    confirmLabel: "Suspend account", danger: true, success: (b) => `${b.business_name} suspended`,
    run: (b) => api.put(`/admin/cs/businesses/${b.id}`, { billing_suspended: true }),
  },
  unsuspend: {
    title: (b) => `Lift suspension for "${b.business_name}"?`,
    body: "This immediately restores their public QR listing — customers scanning the QR code will see the normal rating form again.",
    confirmLabel: "Lift suspension", danger: false, success: (b) => `${b.business_name} suspension lifted`,
    run: (b) => api.put(`/admin/cs/businesses/${b.id}`, { billing_suspended: false }),
  },
  lock: {
    title: (b) => `Lock "${b.business_name}"?`,
    body: "This immediately blocks their dashboard login entirely, hides them from the public oodel SCORE directory, and their QR code shows nothing but the oodel logo when scanned — no message at all. All historical data (responses, QR codes, invoices) is kept; you can unlock anytime.",
    confirmLabel: "Lock account", danger: true, success: (b) => `${b.business_name} locked`,
    run: (b) => api.put(`/admin/cs/businesses/${b.id}`, { active: false }),
  },
  unlock: {
    title: (b) => b.activated_at ? `Unlock "${b.business_name}"?` : `Activate "${b.business_name}"?`,
    body: (b) => b.activated_at
      ? "This immediately restores their dashboard login and their listing in the public oodel SCORE directory."
      : "This is their FIRST activation — it immediately emails them their welcome message with login instructions and their temporary password, and lists them in the public oodel SCORE directory. Make sure their QR codes and Brand Assets are ready before doing this.",
    confirmLabel: (b) => b.activated_at ? "Unlock account" : "Activate & notify business",
    danger: false,
    success: (b) => b.activated_at ? `${b.business_name} unlocked` : `${b.business_name} activated — welcome email sent`,
    run: (b) => api.put(`/admin/cs/businesses/${b.id}`, { active: true }),
  },
  delete: {
    title: (b) => `Delete "${b.business_name}"?`,
    body: "This permanently removes the business account and EVERY piece of data tied to it — locations/QR codes, all customer responses & scans, invoices, and AI/private insights. Their public oodel SCORE profile disappears immediately. This cannot be undone.",
    confirmLabel: "Delete permanently", danger: true, success: (b) => `${b.business_name} deleted`,
    run: (b) => api.delete(`/admin/cs/businesses/${b.id}`),
  },
};

// `body`/`confirmLabel` above are plain strings for most actions, but a few
// (e.g. `unlock`, which doubles as "Activate" for a never-activated Draft
// business) need to vary by business — this helper resolves either shape.
const _resolveMeta = (val, business) => (typeof val === "function" ? val(business) : val);

export function AdminCSBusinesses() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwInfo, setPwInfo] = useState(null);
  const [actionTarget, setActionTarget] = useState(null); // { business, action }
  const [actionRunning, setActionRunning] = useState(false);
  const [pmBusiness, setPmBusiness] = useState(null); // open PM-assignment modal for this business
  const [pendingInsightsCount, setPendingInsightsCount] = useState(0);
  const [respondentModal, setRespondentModal] = useState(null); // { business, rows: [...] | null }

  const load = () => {
    api.get("/admin/cs/businesses").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load businesses")).finally(() => setReady(true));
    api.get("/admin/cs/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/cs/ai-insights/pending").then((r) => setPendingInsightsCount(r.data.length)).catch(() => {});
  };
  useEffect(load, []);

  const openRespondentBreakdown = (b) => {
    setRespondentModal({ business: b, rows: null });
    api.get(`/admin/cs/businesses/${b.id}/existing-respondents`).then((r) => setRespondentModal({ business: b, rows: r.data }))
      .catch(() => { toast.error("Failed to load breakdown"); setRespondentModal(null); });
  };

  const resetPw = async (b) => {
    try {
      const { data } = await api.post(`/admin/cs/businesses/${b.id}/reset-password`);
      setPwInfo({ business: b, password: data.temp_password });
    } catch { toast.error("Reset failed"); }
  };

  const runAction = async () => {
    if (!actionTarget) return;
    const meta = ACTION_META[actionTarget.action];
    setActionRunning(true);
    try {
      await meta.run(actionTarget.business);
      toast.success(meta.success(actionTarget.business));
      load();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Action failed");
    } finally {
      setActionRunning(false);
      setActionTarget(null);
    }
  };

  return (
    <div data-testid="admin-cs-businesses-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black tracking-tight">Customer Sentiment — Businesses</h1>
        <div className="flex gap-2">
          <Link to="/admin/customer-sentiment-insights"><Button variant="outline" className="rounded-none relative" data-testid="cs-pending-insights-link">
            Oodel X review queue
            {pendingInsightsCount > 0 && <span className="ml-1.5 bg-mango text-black text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid="cs-pending-insights-badge">{pendingInsightsCount}</span>}
          </Button></Link>
          <Link to="/admin/customer-sentiment-performance"><Button variant="outline" className="rounded-none" data-testid="cs-performance-link">Business performance</Button></Link>
          <Link to="/admin/customer-sentiment-categories"><Button variant="outline" className="rounded-none" data-testid="cs-manage-categories-link">Manage categories</Button></Link>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-none bg-black hover:bg-neutral-800" data-testid="cs-add-business"><Plus size={16} className="mr-1" /> Add business</Button>
        </div>
      </div>
      <p className="text-xs text-neutral-400 mb-4">Click a business name to open its dashboard — QR codes/locations, billing &amp; invoices, and private insights are all managed from that detail page.</p>

      {pwInfo && (
        <div className="bg-mango/10 border-2 border-mango p-5 mb-6" data-testid="cs-pw-card">
          <div className="overline text-mango mb-2">TEMPORARY PASSWORD — SHARE WITH BUSINESS</div>
          <div className="text-sm">Business: <strong>{pwInfo.business.business_name}</strong> ({pwInfo.business.email})</div>
          <div className="font-mono text-2xl bg-black text-white inline-block px-4 py-2 mt-3 select-all">{pwInfo.password}</div>
          <div>
            <Button onClick={() => { navigator.clipboard.writeText(pwInfo.password); toast.success("Copied"); }} className="rounded-none bg-black hover:bg-neutral-800 text-white mt-3 mr-2" data-testid="cs-copy-pw">Copy password</Button>
            <Button variant="outline" onClick={() => setPwInfo(null)} className="rounded-none mt-3" data-testid="cs-dismiss-pw">Dismiss</Button>
          </div>
        </div>
      )}

      <FadeIn ready={ready}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-y-2 border-black">
              <th className="text-left py-3 px-2 overline">Business</th>
              <th className="text-left py-3 px-2 overline">Contact</th>
              <th className="text-left py-3 px-2 overline">Category</th>
              <th className="text-left py-3 px-2 overline">Tier</th>
              <th className="text-right py-3 px-2 overline" title="Everyone who ever left feedback for this business, split into brand-new Oodel signups vs. people who already had an account">Respondents</th>
              <th className="text-left py-3 px-2 overline">Status</th>
              <th className="text-right py-3 px-2 overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-b border-neutral-200" data-testid={`cs-business-row-${b.id}`}>
                <td className="py-3 px-2">
                  <Link to={`/admin/customer-sentiment/${b.id}`} className="font-bold hover:text-mango inline-flex items-center gap-1.5" data-testid={`cs-business-link-${b.id}`}><QrCode size={14} className="text-neutral-400" />{b.business_name}</Link>
                  <div className="text-xs text-neutral-400">/{b.business_slug}</div>
                </td>
                <td className="py-3 px-2">{b.name}<div className="text-xs text-neutral-400">{b.email}</div></td>
                <td className="py-3 px-2">{b.industry || "—"}</td>
                <td className="py-3 px-2 capitalize">{b.tier}</td>
                <td className="py-3 px-2 text-right" data-testid={`cs-signup-count-${b.id}`}>
                  {b.existing_respondent_count > 0 ? (
                    <button onClick={() => openRespondentBreakdown(b)} className="hover:underline" data-testid={`cs-respondent-breakdown-btn-${b.id}`}>
                      <div className="font-semibold">{b.total_respondents || 0}</div>
                      <div className="text-[10px] text-neutral-400">{b.signup_count || 0} new · {b.existing_respondent_count} existing</div>
                    </button>
                  ) : (
                    <div className="font-semibold">{b.total_respondents || 0}</div>
                  )}
                </td>
                <td className="py-3 px-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`cs-status-menu-${b.id}`}
                        className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 ${
                          !b.active ? (b.activated_at ? "bg-neutral-100 text-neutral-500" : "bg-blue-50 text-blue-600") : b.billing_suspended ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {!b.active ? <Prohibit size={12} weight="fill" /> : b.billing_suspended ? <WarningCircle size={12} weight="fill" /> : <CheckCircle size={12} weight="fill" />}
                        {!b.active ? (b.activated_at ? "Locked" : "Draft") : b.billing_suspended ? "Suspended" : "Active"}
                        <CaretDown size={10} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {!b.active ? (
                        <DropdownMenuItem onClick={() => setActionTarget({ business: b, action: "unlock" })} data-testid={`cs-action-unlock-${b.id}`}>{b.activated_at ? "Unlock account" : "Activate & notify business"}</DropdownMenuItem>
                      ) : (
                        <>
                          {b.billing_suspended ? (
                            <DropdownMenuItem onClick={() => setActionTarget({ business: b, action: "unsuspend" })} data-testid={`cs-action-unsuspend-${b.id}`}>Lift suspension</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setActionTarget({ business: b, action: "suspend" })} data-testid={`cs-action-suspend-${b.id}`}>Suspend (unpaid)</DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setActionTarget({ business: b, action: "lock" })} data-testid={`cs-action-lock-${b.id}`}>Lock account</DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setActionTarget({ business: b, action: "delete" })} data-testid={`cs-action-delete-${b.id}`} className="text-red-600 focus:text-red-600">Delete business</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
                <td className="py-3 px-2 text-right space-x-2 whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setPmBusiness(b)} title="Assign PMs" data-testid={`cs-assign-pms-${b.id}`}>👤 PMs ({(b.account_manager_ids || []).length})</Button>
                  <button onClick={() => { setEditing(b); setOpen(true); }} data-testid={`cs-edit-${b.id}`} className="text-neutral-500 hover:text-black"><PencilSimple size={16} /></button>
                  <button onClick={() => resetPw(b)} data-testid={`cs-reset-pw-${b.id}`} className="text-xs underline text-neutral-500 hover:text-black">Reset PW</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-neutral-400">No businesses yet.</td></tr>}
          </tbody>
        </table>
      </FadeIn>

      {open && <BusinessForm editing={editing} categories={categories} onClose={(res) => { setOpen(false); setEditing(null); load(); if (res?.temp_password) setPwInfo({ business: res.business, password: res.temp_password }); }} />}

      <Dialog open={!!actionTarget} onOpenChange={(o) => !o && setActionTarget(null)}>
        <DialogContent data-testid="cs-action-dialog">
          {actionTarget && (
            <>
              <DialogHeader><DialogTitle>{ACTION_META[actionTarget.action].title(actionTarget.business)}</DialogTitle></DialogHeader>
              <p className="text-sm text-neutral-500">{_resolveMeta(ACTION_META[actionTarget.action].body, actionTarget.business)}</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionTarget(null)} disabled={actionRunning} className="rounded-none" data-testid="cs-action-cancel">Cancel</Button>
                <Button
                  onClick={runAction} disabled={actionRunning}
                  className={`rounded-none ${ACTION_META[actionTarget.action].danger ? "bg-red-600 hover:bg-red-700" : "bg-black hover:bg-neutral-800"}`}
                  data-testid="cs-action-confirm"
                >
                  {actionRunning ? "Working…" : _resolveMeta(ACTION_META[actionTarget.action].confirmLabel, actionTarget.business)}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {pmBusiness && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPmBusiness(null)} data-testid="cs-pm-assign-modal">
          <div className="bg-white max-w-md w-full p-6 border-2 border-black hard-shadow" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">PM ASSIGNMENT</div>
            <h3 className="text-xl font-bold mb-1">{pmBusiness.business_name}</h3>
            <div className="text-xs text-neutral-500 mb-5">{pmBusiness.email}</div>
            <PMAssignmentWidget
              customerId={pmBusiness.id} currentIds={pmBusiness.account_manager_ids || []}
              endpoint={`/admin/inbox/threads/${pmBusiness.id}/assign`}
              onChange={() => { setPmBusiness(null); load(); }}
            />
          </div>
        </div>
      )}

      {respondentModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRespondentModal(null)} data-testid="cs-respondent-breakdown-modal">
          <div className="bg-white max-w-lg w-full p-6 border-2 border-black hard-shadow" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">EXISTING RESPONDENTS</div>
            <h3 className="text-xl font-bold mb-1">{respondentModal.business.business_name}</h3>
            <p className="text-xs text-neutral-500 mb-5">Left feedback here but already had an Oodel account before scanning — so no new signup was created for this business.</p>
            {respondentModal.rows === null && <div className="text-sm text-neutral-400">Loading…</div>}
            {respondentModal.rows && (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {respondentModal.rows.map((r) => (
                  <div key={r.email} className="border border-neutral-200 p-3" data-testid={`cs-existing-respondent-${r.email}`}>
                    <div className="font-semibold text-sm">{r.name || r.email}</div>
                    <div className="text-xs text-neutral-500">{r.email}</div>
                    <div className="text-xs text-neutral-400 mt-1">{r.role ? `${r.role} · ` : ""}Joined via {r.joined_via || "another channel"}{r.member_since ? ` on ${formatDate(r.member_since)}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" className="rounded-none w-full mt-5" onClick={() => setRespondentModal(null)} data-testid="cs-respondent-breakdown-close">Close</Button>
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================================================
// Categories — public directory filter taxonomy
// ============================================================================
export function AdminCSCategories() {
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => { api.get("/admin/cs/categories").then((r) => setCategories(r.data)).catch(() => toast.error("Failed to load categories")).finally(() => setReady(true)); };
  useEffect(load, []);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/admin/cs/categories", { name: newName.trim(), sort_order: categories.length });
      setNewName("");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to add category"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/cs/categories/${deleteTarget.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Delete failed"); } finally { setDeleteTarget(null); }
  };

  return (
    <div data-testid="admin-cs-categories-page">
      <Link to="/admin/customer-sentiment" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black mb-4"><ArrowLeft size={14} /> Back to businesses</Link>
      <h1 className="text-2xl font-black tracking-tight mb-2">Customer Sentiment — Categories</h1>
      <p className="text-sm text-neutral-500 mb-6">Categories power the filter chips on the public oodel SCORE directory and the "Category" field when adding a business.</p>

      <FadeIn ready={ready}>
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-1.5 border border-neutral-300 rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`cs-category-chip-${c.slug}`}>
              <span className="font-medium">{c.name}</span>
              <button onClick={() => setDeleteTarget(c)} data-testid={`cs-category-delete-${c.slug}`} className="w-5 h-5 flex items-center justify-center rounded-full text-neutral-400 hover:bg-red-50 hover:text-red-600"><Trash size={11} /></button>
            </div>
          ))}
          {categories.length === 0 && <div className="text-sm text-neutral-400 italic">No categories yet.</div>}
        </div>
      </FadeIn>

      <div className="flex gap-2 max-w-md">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Salon & Beauty" className="rounded-none" data-testid="cs-category-new-name" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button onClick={add} className="rounded-none bg-black hover:bg-neutral-800 shrink-0" data-testid="cs-category-add"><Plus size={16} className="mr-1" /> Add</Button>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="cs-category-delete-dialog">
          <DialogHeader><DialogTitle>Delete "{deleteTarget?.name}"?</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Categories assigned to a business cannot be deleted — reassign the business first.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-none">Cancel</Button>
            <Button onClick={doDelete} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="cs-category-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BusinessForm({ editing, categories, onClose }) {
  const [form, setForm] = useState(editing ? {
    business_name: editing.business_name, contact_name: editing.name, contact_email: editing.email,
    industry: editing.industry || "", category_slug: editing.category_slug || "", tier: editing.tier,
    logo_url: editing.logo_url || "", notes: editing.notes || "", active: editing.active,
    facebook_url: editing.facebook_url || "", instagram_url: editing.instagram_url || "", website_url: editing.website_url || "",
    show_captured_emails_to_business: editing.visible_metrics?.show_captured_emails_to_business || false,
    show_qr_download: editing.visible_metrics?.show_qr_download !== false,
  } : { business_name: "", contact_name: "", contact_email: "", industry: "", category_slug: "", tier: "business", logo_url: "", notes: "", active: true, facebook_url: "", instagram_url: "", website_url: "", show_captured_emails_to_business: false, show_qr_download: true });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/admin/cs/businesses/${editing.id}`, {
          business_name: form.business_name, category_slug: form.category_slug,
          contact_name: form.contact_name, contact_email: form.contact_email,
          tier: form.tier, logo_url: form.logo_url, notes: form.notes, active: form.active,
          facebook_url: form.facebook_url, instagram_url: form.instagram_url, website_url: form.website_url,
          visible_metrics: { ...(editing.visible_metrics || {}), show_captured_emails_to_business: form.show_captured_emails_to_business, show_qr_download: form.show_qr_download },
        });
        toast.success(data?.activated_now
          ? "Business activated — welcome email with their login/temp password just went out."
          : data?.email_changed
          ? "Business updated — their old password was invalidated and a 'set new password' link was emailed to the new address."
          : "Business updated");
        onClose({});
      } else {
        const { data } = await api.post("/admin/cs/businesses", {
          business_name: form.business_name, contact_name: form.contact_name, contact_email: form.contact_email,
          category_slug: form.category_slug || null, tier: form.tier, logo_url: form.logo_url, notes: form.notes,
          facebook_url: form.facebook_url, instagram_url: form.instagram_url, website_url: form.website_url,
        });
        toast.success("Business created in Draft mode (a starter rating-questions template was auto-created) — no email sent yet. Set up their QR codes and Brand Assets, then toggle Active to notify them.");
        onClose(data);
      }
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-business-form">
        <DialogHeader><DialogTitle>{editing ? "Edit business" : "Add business"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="rounded-none" data-testid="cs-form-business-name" /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none" data-testid="cs-form-contact-name" /></div>
          <div>
            <Label>Contact email {editing && <span className="text-neutral-400 font-normal">(this is also their login email — changing it changes what they sign in with)</span>}</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none" data-testid="cs-form-contact-email" />
          </div>
          <div>
            <Label>Category <span className="text-neutral-400 font-normal">(manage under Customer Sentiment → Categories)</span></Label>
            <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
              <SelectTrigger className="rounded-none" data-testid="cs-form-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {!editing && <p className="text-xs text-neutral-400 mt-1">Rating questions are set up per-business after creation — this business will start with a category-matched starter set, then it's fully custom to them.</p>}
          </div>
          <div>
            <Label>Tier</Label>
            <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
              <SelectTrigger className="rounded-none" data-testid="cs-form-tier"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="business">Business</SelectItem><SelectItem value="business+">Business+</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="rounded-none" data-testid="cs-form-logo" /></div>
          <div className="grid grid-cols-1 gap-3">
            <div><Label>Facebook URL</Label><Input value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} className="rounded-none" placeholder="https://facebook.com/…" data-testid="cs-form-facebook" /></div>
            <div><Label>Instagram URL</Label><Input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} className="rounded-none" placeholder="https://instagram.com/…" data-testid="cs-form-instagram" /></div>
            <div><Label>Website URL</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="rounded-none" placeholder="https://…" data-testid="cs-form-website" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" data-testid="cs-form-notes" /></div>
          {editing && (
            <div className="flex items-center justify-between border-2 border-neutral-200 px-3 py-2.5">
              <div>
                <Label className="mb-0">Active</Label>
                <p className="text-xs text-neutral-400">
                  {!editing.activated_at && !editing.active
                    ? "This business hasn't been activated yet (Draft). Turning this on emails them their welcome message + temp password for the first time — make sure their QR codes/Brand Assets are ready first."
                    : "Locked businesses can't log in and are hidden from the public directory."}
                </p>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cs-form-active-toggle" />
            </div>
          )}
          {editing && (
            <div className="flex items-center justify-between border-2 border-neutral-200 px-3 py-2.5">
              <div>
                <Label className="mb-0">Show respondent emails to business</Label>
                <p className="text-xs text-neutral-400">Lets this business see individual customers' captured emails in their Responses tab (name/PII-sensitive — off by default).</p>
              </div>
              <Switch checked={form.show_captured_emails_to_business} onCheckedChange={(v) => setForm({ ...form, show_captured_emails_to_business: v })} data-testid="cs-form-show-emails-toggle" />
            </div>
          )}
          {editing && (
            <div className="flex items-center justify-between border-2 border-neutral-200 px-3 py-2.5">
              <div>
                <Label className="mb-0">Show QR download to business</Label>
                <p className="text-xs text-neutral-400">When off, this business's own QR Codes page hides the QR image and "Download QR" button (Brand Assets stay downloadable regardless). Admin can still always see/download the QR from here.</p>
              </div>
              <Switch checked={form.show_qr_download} onCheckedChange={(v) => setForm({ ...form, show_qr_download: v })} data-testid="cs-form-show-qr-download-toggle" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()} className="rounded-none" data-testid="cs-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-none bg-black hover:bg-neutral-800" data-testid="cs-form-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Business Report — Admin picks a business + timeframe, runs a deep
// AI-generated analysis (on-screen + downloadable PDF). Persisted so the PDF
// never re-triggers a second LLM call.
// ============================================================================
function BusinessReportSection({ businessId }) {
  const [history, setHistory] = useState([]);
  const [since, setSince] = useState(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [until, setUntil] = useState(() => new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const loadHistory = () => api.get(`/admin/cs/businesses/${businessId}/reports`).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, [businessId]);

  const runReport = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/cs/businesses/${businessId}/report`, { since, until });
      setReport(data);
      toast.success("Report generated");
      loadHistory();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to generate report"); } finally { setRunning(false); }
  };

  const viewPast = async (rid) => {
    try { const { data } = await api.get(`/admin/cs/reports/${rid}`); setReport(data); } catch { toast.error("Failed to load report"); }
  };

  const downloadPdf = async (rid) => {
    try {
      const res = await api.get(`/admin/cs/reports/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `report-${rid.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download report PDF"); }
  };

  return (
    <div className="mb-8" data-testid="cs-detail-report-section">
      <div className="overline mb-3 flex items-center gap-2"><Sparkle size={16} weight="fill" /> BUSINESS REPORT</div>
      <p className="text-xs text-neutral-500 mb-4">Run an on-demand deep AI analysis for a chosen timeframe — score trend, demographic call-outs, strengths/weaknesses and concrete recommendations.</p>
      <div className="border-2 border-black p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><Label>From</Label><Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="rounded-none" data-testid="cs-report-since" /></div>
        <div><Label>To</Label><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="rounded-none" data-testid="cs-report-until" /></div>
        <Button onClick={runReport} disabled={running} className="rounded-none bg-mango hover:bg-mango/90" data-testid="cs-report-run">{running ? "Generating… (~15s)" : "Run report"}</Button>
        {history.length > 0 && (
          <div className="flex items-center gap-2 ml-auto text-xs text-neutral-400">
            Past reports:
            {history.slice(0, 5).map((h) => (
              <button key={h.id} onClick={() => viewPast(h.id)} data-testid={`cs-report-history-${h.id}`} className="underline hover:text-black">{h.since}→{h.until}</button>
            ))}
          </div>
        )}
      </div>
      {report && (
        <div className="border-2 border-mango bg-mango/5 p-5" data-testid="cs-report-result">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold">Report: {report.since} → {report.until}</div>
            <Button variant="outline" onClick={() => downloadPdf(report.id)} className="rounded-none text-xs" data-testid="cs-report-download-pdf">Download PDF</Button>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-4 text-center">
            <div className="bg-white border border-neutral-200 p-3"><div className="text-[10px] text-neutral-400">AVG SCORE</div><div className="text-lg font-black">{report.dashboard.score_card.avg_stars ?? "—"}</div></div>
            <div className="bg-white border border-neutral-200 p-3"><div className="text-[10px] text-neutral-400">RESPONSES</div><div className="text-lg font-black">{report.dashboard.score_card.total_responses}</div></div>
            <div className="bg-white border border-neutral-200 p-3"><div className="text-[10px] text-neutral-400">TREND</div><div className="text-lg font-black">{report.dashboard.score_card.trend_delta ?? "—"}</div></div>
            <div className="bg-white border border-neutral-200 p-3"><div className="text-[10px] text-neutral-400">% POSITIVE</div><div className="text-lg font-black">{report.dashboard.score_card.pct_positive != null ? `${report.dashboard.score_card.pct_positive}%` : "—"}</div></div>
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-line" data-testid="cs-report-narrative">{report.narrative}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Business detail — QR codes + billing/invoices + insights + private insights
// ============================================================================
const BILLING_CYCLE_LABEL = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };
const INVOICE_STATUS_STYLE = {
  paid: { icon: CheckCircle, label: "Paid", cls: "text-emerald-600 bg-emerald-50" },
  pending: { icon: Clock, label: "Pending", cls: "text-amber-600 bg-amber-50" },
  overdue: { icon: WarningCircle, label: "Overdue", cls: "text-red-600 bg-red-50" },
};

export function AdminCSBusinessDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [locs, setLocs] = useState([]);
  const [insights, setInsights] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLocId, setAiInsightsLocId] = useState(null);
  const [regenGranularity, setRegenGranularity] = useState("weekly");
  const [regeneratingInsight, setRegeneratingInsight] = useState(false);
  const [newLocName, setNewLocName] = useState("");
  const [newLocCity, setNewLocCity] = useState("");
  const [editingLocId, setEditingLocId] = useState(null);
  const [editSlug, setEditSlug] = useState("");
  const [editingCityLocId, setEditingCityLocId] = useState(null);
  const [editCity, setEditCity] = useState("");
  const [editingNameLocId, setEditingNameLocId] = useState(null);
  const [editName, setEditName] = useState("");
  const [insightForm, setInsightForm] = useState({ title: "", description: "", report_url: "" });
  const [billing, setBilling] = useState({ billing_cycle: "", agreed_amount: "", first_billing_date: "", first_month_free: false });
  const [billingSaving, setBillingSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [newTplName, setNewTplName] = useState("");
  const [newTplCategory, setNewTplCategory] = useState("");

  const loadTemplates = () => api.get(`/admin/cs/businesses/${id}/templates`).then((r) => {
    setTemplates(r.data);
    setSelectedTemplateId((prev) => prev && r.data.some((t) => t.id === prev) ? prev : (r.data.find((t) => t.is_default)?.id || r.data[0]?.id || null));
  }).catch(() => {});

  const load = () => {
    api.get(`/admin/cs/businesses/${id}/insights`).then((r) => {
      setData(r.data);
      setBilling({
        billing_cycle: r.data.business.billing_cycle || "",
        agreed_amount: r.data.business.agreed_amount ?? "",
        first_billing_date: (r.data.business.first_billing_date || "").slice(0, 10),
        first_month_free: !!r.data.business.first_month_free,
      });
    }).catch(() => toast.error("Failed to load"));
    api.get(`/admin/cs/businesses/${id}/locations`).then((r) => setLocs(r.data)).catch(() => {});
    api.get(`/admin/cs/businesses/${id}/private-insights`).then((r) => setInsights(r.data)).catch(() => {});
    api.get(`/admin/cs/businesses/${id}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
    loadTemplates();
  };
  useEffect(load, [id]);
  useEffect(() => {
    const params = { granularity: regenGranularity, ...(aiInsightsLocId ? { location_id: aiInsightsLocId } : {}) };
    api.get(`/admin/cs/businesses/${id}/ai-insights`, { params }).then((r) => setAiInsights(r.data)).catch(() => setAiInsights([]));
  }, [id, aiInsightsLocId, regenGranularity]);

  const reloadAiInsights = () => {
    const params = { granularity: regenGranularity, ...(aiInsightsLocId ? { location_id: aiInsightsLocId } : {}) };
    api.get(`/admin/cs/businesses/${id}/ai-insights`, { params }).then((r) => setAiInsights(r.data)).catch(() => {});
  };

  const addLocation = async () => {
    if (!newLocName.trim() || !newLocCity.trim()) { toast.error("Branch name and city are both required."); return; }
    try {
      await api.post(`/admin/cs/businesses/${id}/locations`, { name: newLocName.trim(), city: newLocCity.trim() });
      setNewLocName("");
      setNewLocCity("");
      toast.success("Location + QR code created");
      load();
    } catch { toast.error("Failed to create location"); }
  };

  const deleteLocation = async (lid) => {
    try { await api.delete(`/admin/cs/businesses/${id}/locations/${lid}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const saveSlug = async (lid) => {
    if (!editSlug.trim()) return;
    try {
      await api.put(`/admin/cs/businesses/${id}/locations/${lid}`, { qr_code: editSlug.trim() });
      toast.success("Custom QR link saved");
      setEditingLocId(null);
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save"); }
  };

  const saveCity = async (lid) => {
    if (!editCity.trim()) return;
    try {
      await api.put(`/admin/cs/businesses/${id}/locations/${lid}`, { city: editCity.trim() });
      toast.success("City updated — will apply to new responses from this QR");
      setEditingCityLocId(null);
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save"); }
  };

  const saveName = async (lid) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/admin/cs/businesses/${id}/locations/${lid}`, { name: editName.trim() });
      toast.success("Branch name updated");
      setEditingNameLocId(null);
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save"); }
  };

  const saveLocTemplate = async (lid, tid) => {
    try {
      const body = tid ? { template_id: tid } : { clear_template: true };
      await api.put(`/admin/cs/businesses/${id}/locations/${lid}`, body);
      toast.success("Feedback template updated for this QR");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save"); }
  };

  const createTemplate = async () => {
    if (!newTplName.trim()) { toast.error("Give the new template a name."); return; }
    try {
      const { data: tpl } = await api.post(`/admin/cs/businesses/${id}/templates`, { name: newTplName.trim(), starter_category: newTplCategory || null });
      setNewTplName("");
      setNewTplCategory("");
      toast.success("Template created — assign it to a QR code below");
      await loadTemplates();
      setSelectedTemplateId(tpl.id);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to create template"); }
  };

  const deleteTemplate = async (tid) => {
    try {
      await api.delete(`/admin/cs/businesses/${id}/templates/${tid}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to delete template"); }
  };

  const addInsight = async () => {
    if (!insightForm.title.trim()) return;
    try {
      await api.post(`/admin/cs/businesses/${id}/private-insights`, insightForm);
      setInsightForm({ title: "", description: "", report_url: "" });
      toast.success("Insight added");
      load();
    } catch { toast.error("Failed to add insight"); }
  };

  const deleteInsight = async (iid) => {
    try { await api.delete(`/admin/cs/businesses/${id}/private-insights/${iid}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  // Checking "Set first month free" auto-fills the first billing date to
  // one cycle after the account's creation date (e.g. created Jan 1 +
  // monthly -> Feb 1) — admin can still hand-edit the date afterwards.
  const CYCLE_DAYS = { monthly: 30, quarterly: 90, yearly: 365 };
  const toggleFirstMonthFree = (checked) => {
    if (checked && billing.billing_cycle && data?.business?.created_at) {
      const created = new Date(data.business.created_at);
      const due = new Date(created.getTime() + CYCLE_DAYS[billing.billing_cycle] * 86400000);
      setBilling({ ...billing, first_month_free: true, first_billing_date: due.toISOString().slice(0, 10) });
    } else {
      setBilling({ ...billing, first_month_free: checked });
    }
  };

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      await api.put(`/admin/cs/businesses/${id}/billing`, {
        billing_cycle: billing.billing_cycle || null,
        agreed_amount: billing.agreed_amount === "" ? null : Number(billing.agreed_amount),
        first_billing_date: billing.first_billing_date || null,
        first_month_free: billing.first_month_free,
      });
      toast.success("Billing settings saved — the first invoice auto-generates 7 days before it's due.");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save billing"); } finally { setBillingSaving(false); }
  };

  const generateInvoice = async () => {
    setGenerating(true);
    try {
      await api.post(`/admin/cs/businesses/${id}/invoices/generate`);
      toast.success("Invoice generated");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to generate invoice"); } finally { setGenerating(false); }
  };

  const markPaid = async (iid) => { try { await api.put(`/admin/cs/invoices/${iid}/mark-paid`); toast.success("Marked paid"); load(); } catch { toast.error("Failed"); } };
  const markPending = async (iid) => { try { await api.put(`/admin/cs/invoices/${iid}/mark-pending`); toast.success("Marked pending"); load(); } catch { toast.error("Failed"); } };
  const [sendingId, setSendingId] = useState(null);
  const sendInvoice = async (iid) => {
    setSendingId(iid);
    try { await api.post(`/admin/cs/invoices/${iid}/send`); toast.success("Invoice emailed to the business (BCC info@oodel.co)"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to send invoice"); }
    finally { setSendingId(null); }
  };
  const downloadInvoicePdf = async (iid) => {
    try {
      const res = await api.get(`/admin/cs/invoices/${iid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${iid.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice PDF"); }
  };

  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;
  const { business } = data;
  const fmtDate = (d) => formatDate(d);

  return (
    <div data-testid="admin-cs-business-detail">
      <Link to="/admin/customer-sentiment" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black mb-4"><ArrowLeft size={14} /> Back to businesses</Link>
      <h1 className="text-2xl font-black tracking-tight mb-1">{business.business_name}</h1>
      <div className="text-sm text-neutral-400 mb-6 capitalize" data-testid="cs-detail-status-line">
        {business.tier} tier · {business.industry || "—"} ·{" "}
        {!business.active ? (business.activated_at ? <span className="text-red-600">Locked</span> : <span className="text-blue-600">Draft — not yet activated</span>) : business.billing_suspended ? <span className="text-amber-600">Suspended (unpaid invoice)</span> : <span className="text-emerald-600">Active</span>}
        {business.created_at && <span className="text-neutral-400"> · Created {fmtDate(business.created_at)}</span>}
        {business.activated_at && <span className="text-neutral-400"> · Active since {fmtDate(business.activated_at)}</span>}
      </div>
      {!business.active && !business.activated_at && (
        <div className="border-2 border-blue-200 bg-blue-50 text-blue-800 text-sm px-4 py-3 mb-6" data-testid="cs-detail-draft-banner">
          This business is in <strong>Draft mode</strong> — no email has been sent to them yet. Set up their QR codes / Brand Assets below, then edit this business and toggle <strong>Active</strong> to email them their login details.
        </div>
      )}

      <div className="border-2 border-mango bg-mango/5 p-5 mb-8" data-testid="cs-detail-qr-section">
        <div className="overline mb-1 flex items-center gap-2 text-mango"><QrCode size={16} /> LOCATIONS / QR CODES</div>
        <p className="text-xs text-neutral-500 mb-4">Generate a QR code per branch/location — customers scan it and rate the business on a one-screen form. Print + place at the counter/till. Respondents are no longer asked their city — it's set once here per branch, so every rating from this QR is automatically tagged with the branch's city.</p>
        <div className="flex gap-2 mb-4 max-w-lg">
          <Input value={newLocName} onChange={(e) => setNewLocName(e.target.value)} placeholder="Branch name, e.g. Main Branch — Clifton" className="rounded-none" data-testid="cs-detail-new-location" onKeyDown={(e) => e.key === "Enter" && addLocation()} />
          <Input value={newLocCity} onChange={(e) => setNewLocCity(e.target.value)} placeholder="City, e.g. Karachi" className="rounded-none" data-testid="cs-detail-new-location-city" onKeyDown={(e) => e.key === "Enter" && addLocation()} />
          <Button onClick={addLocation} className="rounded-none bg-mango hover:bg-mango/90 shrink-0" data-testid="cs-detail-add-location"><Plus size={16} className="mr-1" /> Generate QR</Button>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {locs.map((l) => (
            <div key={l.id} className="border-2 border-black bg-white p-4 text-center" data-testid={`cs-detail-location-${l.id}`}>
              {editingNameLocId === l.id ? (
                <div className="flex items-center justify-center gap-1 mb-1" data-testid={`cs-detail-name-edit-${l.id}`}>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-none h-7 text-sm font-bold px-1.5 max-w-[140px]" data-testid={`cs-detail-name-input-${l.id}`} onKeyDown={(e) => e.key === "Enter" && saveName(l.id)} />
                  <button onClick={() => saveName(l.id)} data-testid={`cs-detail-name-save-${l.id}`} className="text-xs font-bold text-mango">Save</button>
                  <button onClick={() => setEditingNameLocId(null)} className="text-xs text-neutral-400">✕</button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingNameLocId(l.id); setEditName(l.name || ""); }}
                  data-testid={`cs-detail-edit-name-${l.id}`}
                  className="font-bold text-sm mb-1 hover:text-mango underline block w-full truncate"
                  title="Click to rename this branch"
                >
                  {l.name}
                </button>
              )}
              {editingCityLocId === l.id ? (
                <div className="flex items-center justify-center gap-1 mb-2" data-testid={`cs-detail-city-edit-${l.id}`}>
                  <Input value={editCity} onChange={(e) => setEditCity(e.target.value)} className="rounded-none h-7 text-xs px-1.5 max-w-[110px]" data-testid={`cs-detail-city-input-${l.id}`} onKeyDown={(e) => e.key === "Enter" && saveCity(l.id)} />
                  <button onClick={() => saveCity(l.id)} data-testid={`cs-detail-city-save-${l.id}`} className="text-xs font-bold text-mango">Save</button>
                  <button onClick={() => setEditingCityLocId(null)} className="text-xs text-neutral-400">✕</button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingCityLocId(l.id); setEditCity(l.city || ""); }}
                  data-testid={`cs-detail-edit-city-${l.id}`}
                  className="text-xs text-neutral-500 hover:text-mango underline mb-2 block w-full"
                  title="Click to change this branch's city"
                >
                  {l.city || "Set city…"}
                </button>
              )}
              <img src={l.qr_image_data || l.qr_image_url} alt="QR" className="w-24 h-24 mx-auto mb-2" data-testid={`cs-detail-qr-image-${l.id}`} />
              <a href={l.qr_image_data || l.qr_image_url} download={`qr-${l.qr_code}.png`} target="_blank" rel="noreferrer" data-testid={`cs-detail-qr-download-${l.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-mango underline mb-2">
                <Download size={12} /> Download high-res QR
              </a>
              <div className="text-xs text-neutral-400 mb-1">{l.response_count || 0} responses</div>
              {templates.length > 1 && (
                <div className="mb-2" data-testid={`cs-detail-loc-template-wrap-${l.id}`}>
                  <select
                    value={l.template_id || ""}
                    onChange={(e) => saveLocTemplate(l.id, e.target.value || null)}
                    data-testid={`cs-detail-loc-template-select-${l.id}`}
                    className="w-full text-xs border-2 border-neutral-200 rounded-none py-1 px-1 bg-white"
                    title="Which feedback questions this QR code shows"
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.is_default ? "" : t.id}>{t.is_default ? `${t.name} (default)` : t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {editingLocId === l.id ? (
                <div className="flex items-center gap-1 mb-2" data-testid={`cs-detail-slug-edit-${l.id}`}>
                  <span className="text-xs text-neutral-400">/scan/</span>
                  <Input value={editSlug} onChange={(e) => setEditSlug(e.target.value)} className="rounded-none h-7 text-xs px-1.5" data-testid={`cs-detail-slug-input-${l.id}`} onKeyDown={(e) => e.key === "Enter" && saveSlug(l.id)} />
                  <button onClick={() => saveSlug(l.id)} data-testid={`cs-detail-slug-save-${l.id}`} className="text-xs font-bold text-mango">Save</button>
                  <button onClick={() => setEditingLocId(null)} className="text-xs text-neutral-400">✕</button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingLocId(l.id); setEditSlug(l.qr_code.toLowerCase()); }}
                  data-testid={`cs-detail-edit-slug-${l.id}`}
                  className="text-xs text-neutral-400 hover:text-mango underline mb-2 block w-full truncate"
                  title="Click to customize this QR link"
                >
                  /scan/{l.qr_code.toLowerCase()}
                </button>
              )}
              <LocationAssetsPanel businessId={id} location={l} onChanged={load} />
              <button
                onClick={async () => {
                  try {
                    const r = await api.post(`/admin/cs/locations/${l.id}/preview-token`);
                    window.open(r.data.preview_url, "_blank", "noopener,noreferrer");
                  } catch (e) { toast.error(fmtError(e)); }
                }}
                data-testid={`cs-detail-preview-form-${l.id}`}
                className="text-xs text-neutral-500 hover:text-mango underline block w-full mb-1"
                title="See the exact feedback form a customer would see — works even while the business is in Draft. Submissions are disabled."
              >
                Preview form (admin-only, 2h link)
              </button>
              <button onClick={() => deleteLocation(l.id)} data-testid={`cs-detail-delete-location-${l.id}`} className="text-xs text-red-600 underline">Delete</button>
            </div>
          ))}
          {locs.length === 0 && <div className="text-sm text-neutral-400 col-span-3">No locations yet — generate the first QR code above.</div>}
        </div>
      </div>

      <div className="border-2 border-black bg-white p-5 mb-8" data-testid="cs-detail-templates-section">
        <div className="overline mb-1">FEEDBACK TEMPLATES</div>
        <p className="text-xs text-neutral-500 mb-4">
          Most businesses just need one shared question set. But if this business runs different sessions/branches that should ask
          DIFFERENT questions (e.g. a training company with an "Excel Training" QR and a separate "React Training" QR), create an
          extra template here, edit its questions below, then assign it to the right QR code(s) above.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedTemplateId(t.id)}
              data-testid={`cs-detail-template-tab-${t.id}`}
              className={`text-xs font-bold px-3 py-1.5 border-2 ${selectedTemplateId === t.id ? "border-mango bg-mango/10 text-mango" : "border-neutral-200 text-neutral-600 hover:border-neutral-400"}`}
              title={t.locations?.length ? `Used by: ${t.locations.map((l) => l.name).join(", ")}` : "Not assigned to any QR yet"}
            >
              {t.name}{t.is_default ? " · default" : ""} ({t.active_question_count})
            </button>
          ))}
        </div>
        {selectedTemplateId && templates.find((t) => t.id === selectedTemplateId) && !templates.find((t) => t.id === selectedTemplateId).is_default && (
          <div className="mb-4">
            <button
              onClick={() => deleteTemplate(selectedTemplateId)}
              data-testid={`cs-detail-template-delete-${selectedTemplateId}`}
              className="text-xs text-red-600 underline"
            >
              Delete this template
            </button>
          </div>
        )}
        <div className="flex gap-2 max-w-xl">
          <Input value={newTplName} onChange={(e) => setNewTplName(e.target.value)} placeholder='Template name, e.g. "React Training Feedback"' className="rounded-none" data-testid="cs-detail-new-template-name" onKeyDown={(e) => e.key === "Enter" && createTemplate()} />
          <Select value={newTplCategory} onValueChange={setNewTplCategory}>
            <SelectTrigger className="rounded-none w-48" data-testid="cs-detail-new-template-category"><SelectValue placeholder="Starter questions…" /></SelectTrigger>
            <SelectContent>
              {["Restaurant", "Retail", "Healthcare", "General Service"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={createTemplate} className="rounded-none bg-mango hover:bg-mango/90 shrink-0" data-testid="cs-detail-add-template"><Plus size={16} className="mr-1" /> New template</Button>
        </div>
      </div>

      <RatingQuestionsPanel businessId={id} templateId={selectedTemplateId} />

      <div className="mb-8" data-testid="cs-detail-dashboard">
        <CsDashboard isAdmin businessId={id} />
      </div>

      <div className="mb-8" data-testid="cs-detail-ai-insights-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div className="overline flex items-center gap-2 text-mango"><Sparkle size={16} weight="fill" /> OODEL X INSIGHTS</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center border border-neutral-200 rounded-lg overflow-hidden text-xs font-semibold" data-testid="cs-detail-ai-insights-granularity-toggle">
              {["weekly", "monthly", "quarterly", "yearly"].map((g) => {
                const disabled = g !== "weekly" && !!aiInsightsLocId;
                return (
                  <button
                    key={g} disabled={disabled} onClick={() => setRegenGranularity(g)}
                    className={`px-2.5 py-1.5 capitalize ${disabled ? "bg-neutral-50 text-neutral-300 cursor-not-allowed" : regenGranularity === g ? "bg-black text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}
                    data-testid={`cs-detail-ai-insights-granularity-${g}`}
                    title={disabled ? "Monthly/Quarterly/Yearly are combined whole-business reports — pick 'All branches' to see them" : ""}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
            {locs.length > 1 && (
              <select
                value={aiInsightsLocId || ""}
                onChange={(e) => { const v = e.target.value || null; setAiInsightsLocId(v); if (v) setRegenGranularity("weekly"); }}
                className="text-xs font-semibold border border-neutral-200 rounded-lg px-2 py-1.5"
                data-testid="cs-detail-ai-insights-branch-select"
              >
                <option value="">All branches</option>
                {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <Button
              size="sm" variant="outline" className="rounded-none text-xs" disabled={regeneratingInsight}
              onClick={async () => {
                setRegeneratingInsight(true);
                try {
                  const params = { granularity: regenGranularity, ...(aiInsightsLocId ? { location_id: aiInsightsLocId } : {}) };
                  const r = await api.post(`/admin/cs/businesses/${id}/ai-insights/regenerate`, {}, { params });
                  toast.success(r.data.regenerated ? "Regenerated — check the review queue." : r.data.message);
                  api.get(`/admin/cs/businesses/${id}/ai-insights`, { params }).then((rr) => setAiInsights(rr.data));
                } catch (e) {
                  toast.error(fmtError(e.response?.data?.detail) || "Failed to regenerate");
                } finally {
                  setRegeneratingInsight(false);
                }
              }}
              data-testid="cs-regenerate-insight-btn"
            >
              {regeneratingInsight ? "Regenerating…" : `Regenerate latest ${regenGranularity}`}
            </Button>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mb-4">Weekly is generated per branch; pick "All branches" to see every branch's weekly report mixed together. Monthly/Quarterly/Yearly are always one combined whole-business report. Deleted a bad response? "Regenerate latest" recomputes the most recent {regenGranularity} report with current data.</p>
        {aiInsights === null && <div className="text-sm text-neutral-400">Loading…</div>}
        <ScansResponsesChart rows={aiInsights} />
        {aiInsights && <AiInsightsFeed rows={aiInsights} isAdmin businessId={id} onChanged={reloadAiInsights} />}
        {aiInsights && aiInsights.length === 0 && <div className="text-sm text-neutral-400">No {regenGranularity} insights yet — generates automatically once the {regenGranularity === "weekly" ? "current week" : regenGranularity === "monthly" ? "current month" : regenGranularity === "quarterly" ? "current quarter" : "current year"} closes (no minimum response count). Freshly generated ones wait in <Link to="/admin/customer-sentiment-insights" className="underline hover:text-black">the review queue</Link> until approved.</div>}
      </div>

      <div className="mb-8" data-testid="cs-detail-billing-section">
        <div className="overline mb-3 flex items-center gap-2"><Receipt size={16} /> BILLING &amp; INVOICES</div>
        <p className="text-xs text-neutral-400 mb-3">Created <strong>{fmtDate(business.created_at)}</strong>. Auto-dunning: an invoice is emailed 7 days before its due date, an overdue notice at the due date, and the QR listing is auto-suspended 7 days after that if still unpaid.</p>
        <div className="border-2 border-black p-4 mb-4 grid md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Billing cycle</Label>
            <Select value={billing.billing_cycle || "none"} onValueChange={(v) => setBilling({ ...billing, billing_cycle: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-none" data-testid="cs-billing-cycle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No billing</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Agreed amount (PKR)</Label>
            <Input type="number" value={billing.agreed_amount} onChange={(e) => setBilling({ ...billing, agreed_amount: e.target.value })} className="rounded-none" data-testid="cs-billing-amount" placeholder="25000" />
          </div>
          <div>
            <Label>First billing date</Label>
            <Input type="date" value={billing.first_billing_date} onChange={(e) => setBilling({ ...billing, first_billing_date: e.target.value, first_month_free: false })} className="rounded-none" data-testid="cs-billing-first-date" />
          </div>
          <div className="flex items-center gap-2 md:col-span-2">
            <Checkbox checked={billing.first_month_free} onCheckedChange={toggleFirstMonthFree} disabled={!billing.billing_cycle} data-testid="cs-billing-first-month-free" />
            <Label className="mb-0 cursor-pointer" onClick={() => billing.billing_cycle && toggleFirstMonthFree(!billing.first_month_free)}>Set first month free — auto-fills the first billing date to one cycle after creation</Label>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveBilling} disabled={billingSaving} className="rounded-none bg-black hover:bg-neutral-800" data-testid="cs-billing-save">{billingSaving ? "Saving…" : "Save billing"}</Button>
            <Button onClick={generateInvoice} disabled={generating || !business.billing_cycle} variant="outline" className="rounded-none" data-testid="cs-invoice-generate-now">{generating ? "Generating…" : "Generate invoice now"}</Button>
          </div>
        </div>
        {business.billing_cycle && <p className="text-xs text-neutral-400 mb-3">Cycle: <strong>{BILLING_CYCLE_LABEL[business.billing_cycle]}</strong> · Next invoice due: <strong>{fmtDate(business.next_invoice_due)}</strong> — auto-generated + emailed 7 days ahead of that date; you can also generate one on demand above.</p>}
        {business.billing_suspended && (
          <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3" data-testid="cs-billing-suspended-notice">
            This business is currently billing-suspended (unpaid past grace period) — their QR listing shows an "Account Suspended" notice. Mark the overdue invoice below as Paid to lift it automatically.
          </p>
        )}
        <div className="border-2 border-neutral-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="py-2 px-3 overline">Period</th>
                <th className="py-2 px-3 overline">Due date</th>
                <th className="py-2 px-3 overline">Amount</th>
                <th className="py-2 px-3 overline">Status</th>
                <th className="py-2 px-3 overline text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const st = INVOICE_STATUS_STYLE[inv.status] || INVOICE_STATUS_STYLE.pending;
                const Icon = st.icon;
                return (
                  <tr key={inv.id} className="border-b border-neutral-200" data-testid={`cs-invoice-row-${inv.id}`}>
                    <td className="py-2 px-3">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                    <td className="py-2 px-3 text-neutral-500">{fmtDate(inv.due_date || inv.period_start)}</td>
                    <td className="py-2 px-3 font-bold">{inv.currency} {inv.amount?.toLocaleString()}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 ${st.cls}`}><Icon size={12} weight="fill" /> {st.label}</span>
                      {inv.sent_at && <span className="ml-2 text-xs text-neutral-400">Sent {fmtDate(inv.sent_at)}</span>}
                    </td>
                    <td className="py-2 px-3 text-right space-x-3 whitespace-nowrap">
                      <button onClick={() => downloadInvoicePdf(inv.id)} data-testid={`cs-invoice-pdf-${inv.id}`} className="text-xs underline text-neutral-500 hover:text-black">PDF</button>
                      <button onClick={() => sendInvoice(inv.id)} disabled={sendingId === inv.id} data-testid={`cs-invoice-send-${inv.id}`} className="text-xs underline text-neutral-500 hover:text-black">{sendingId === inv.id ? "Sending…" : "Send"}</button>
                      {inv.status === "paid" ? (
                        <button onClick={() => markPending(inv.id)} data-testid={`cs-invoice-mark-pending-${inv.id}`} className="text-xs underline text-neutral-500 hover:text-black">Mark pending</button>
                      ) : (
                        <button onClick={() => markPaid(inv.id)} data-testid={`cs-invoice-mark-paid-${inv.id}`} className="text-xs underline text-emerald-600 hover:text-emerald-800">Mark paid</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {invoices.length === 0 && <tr><td colSpan={4} className="py-4 px-3 text-center text-neutral-400">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <BusinessReportSection businessId={id} />

      <AdminDocumentsPanel businessId={id} />

      <div>
        <div className="overline mb-3">PRIVATE INSIGHTS {business.tier !== "business+" && <span className="text-neutral-400">(visible only on Business+)</span>}</div>
        <div className="border-2 border-black p-4 mb-4 space-y-2">
          <Input value={insightForm.title} onChange={(e) => setInsightForm({ ...insightForm, title: e.target.value })} placeholder="Title" className="rounded-none" data-testid="cs-insight-title" />
          <Textarea value={insightForm.description} onChange={(e) => setInsightForm({ ...insightForm, description: e.target.value })} placeholder="Description" className="rounded-none" data-testid="cs-insight-desc" />
          <Input value={insightForm.report_url} onChange={(e) => setInsightForm({ ...insightForm, report_url: e.target.value })} placeholder="Report URL (optional)" className="rounded-none" data-testid="cs-insight-url" />
          <Button onClick={addInsight} className="rounded-none bg-black hover:bg-neutral-800" data-testid="cs-insight-add">Add insight</Button>
        </div>
        <div className="space-y-3">
          {insights.map((i) => (
            <div key={i.id} className="bg-white rounded-2xl border border-neutral-200 p-5 relative" data-testid={`cs-insight-row-${i.id}`}>
              <button onClick={() => deleteInsight(i.id)} data-testid={`cs-insight-delete-${i.id}`} className="absolute top-4 right-4 text-neutral-400 hover:text-red-600"><Trash size={16} /></button>
              <div className="font-bold mb-1 pr-8">{i.title}</div>
              {i.description && <p className="text-sm text-neutral-500 mb-2">{i.description}</p>}
              <div className="text-xs text-neutral-400 mb-2">{formatDate(i.created_at)}</div>
              {i.report_url && <a href={i.report_url} target="_blank" rel="noreferrer" className="text-sm font-bold text-mango hover:underline">View report →</a>}
            </div>
          ))}
          {insights.length === 0 && <div className="text-sm text-neutral-400">No private insights added yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Branded onboarding kit — admin uploads the FINAL print-ready file (QR
// already composited in Photoshop by the admin) per location/branch, since
// each branch has its own unique QR. The business just downloads whatever's
// been uploaded from their QR Codes page — no server-side QR placement.
// ============================================================================
const ASSET_TYPES = [
  ["a3", "A3"], ["a4", "A4"], ["a5", "A5"],
  ["sticker_window", "Window sticker"], ["sticker_door", "Door sticker"], ["sticker_square", "Counter sticker"],
];

function LocationAssetsPanel({ businessId, location, onChanged }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});
  const assets = location.assets || {};
  const count = Object.keys(assets).length;

  const upload = async (assetType, file) => {
    if (!file) return;
    setUploading(assetType);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/cs/businesses/${businessId}/locations/${location.id}/assets/${assetType}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${ASSET_TYPES.find((a) => a[0] === assetType)[1]} uploaded`);
      onChanged();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Upload failed"); } finally { setUploading(null); }
  };

  const removeAsset = async (assetType) => {
    try { await api.delete(`/admin/cs/businesses/${businessId}/locations/${location.id}/assets/${assetType}`); toast.success("Removed"); onChanged(); }
    catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="mb-2">
      <button onClick={() => setOpen(!open)} data-testid={`cs-kit-toggle-${location.id}`} className="text-xs text-neutral-500 hover:text-mango underline mb-2 block w-full">
        Brand Assets {count > 0 && `(${count}/${ASSET_TYPES.length})`} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-1.5 mb-2 text-left" data-testid={`cs-kit-panel-${location.id}`}>
          {ASSET_TYPES.map(([type, label]) => (
            <div key={type} className="border border-neutral-200 rounded px-1.5 py-1 flex items-center justify-between gap-1">
              <span className="text-[10px] font-semibold truncate">{label}</span>
              <div className="flex items-center gap-1 shrink-0">
                {assets[type] && (
                  <a href={assets[type]} download={`${location.qr_code}-${type}.${assets[type].split(".").pop()}`} target="_blank" rel="noreferrer" data-testid={`cs-kit-download-${location.id}-${type}`} className="text-mango" title="Download"><Download size={12} /></a>
                )}
                <input ref={(el) => (fileInputs.current[type] = el)} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(type, e.target.files[0])} />
                <button
                  onClick={() => fileInputs.current[type]?.click()}
                  disabled={uploading === type}
                  data-testid={`cs-kit-upload-${location.id}-${type}`}
                  className="text-neutral-500 hover:text-black"
                  title={assets[type] ? "Replace" : "Upload"}
                >
                  {uploading === type ? "…" : <UploadSimple size={12} />}
                </button>
                {assets[type] && (
                  <button onClick={() => removeAsset(type)} data-testid={`cs-kit-remove-${location.id}-${type}`} className="text-neutral-400 hover:text-red-600" title="Remove"><X size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Rating Questions — every business owns its own custom question set
// (tied to a category, no longer shared/generic). Editing text is safe at
// any time; removing a question that already has responses is only
// possible via Archive (keeps historical labels readable) — never a
// destructive delete. Urdu mirrors the survey Question Bank pattern:
// AI auto-drafts, admin reviews + approves per question.
// ============================================================================
const UR_STATUS_LABEL = { approved: "Approved", draft: "AI draft — needs review", stale: "Text changed — re-check", missing: "No Urdu yet" };

function RatingQuestionsPanel({ businessId, templateId }) {
  const [tpl, setTpl] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [newQType, setNewQType] = useState("star");
  const [urEditing, setUrEditing] = useState(null); // question id currently expanded
  const [urDraft, setUrDraft] = useState("");
  const [savingUr, setSavingUr] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [togglingUr, setTogglingUr] = useState(false);

  const load = () => { if (templateId) api.get(`/admin/cs/templates/${templateId}`).then((r) => setTpl(r.data)).catch(() => {}); };
  useEffect(load, [templateId]);

  if (!tpl) return null;
  const active = tpl.questions.filter((q) => q.active !== false);
  const archived = tpl.questions.filter((q) => q.active === false);

  const saveAllTextEdits = async (updatedQuestions) => {
    try {
      await api.put(`/admin/cs/templates/${tpl.id}`, {
        name: tpl.name, industry: tpl.industry,
        questions: updatedQuestions.map((q) => ({ id: q.id, text: q.text, type: q.type || "star" })),
      });
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Save failed"); }
  };

  const editText = (qid, text) => setTpl((t) => ({ ...t, questions: t.questions.map((q) => (q.id === qid ? { ...q, text } : q)) }));
  const commitText = () => saveAllTextEdits(tpl.questions);

  const addQuestion = async () => {
    if (!newQ.trim()) return;
    try {
      await api.put(`/admin/cs/templates/${tpl.id}`, { name: tpl.name, industry: tpl.industry, questions: [...tpl.questions.map((q) => ({ id: q.id, text: q.text, type: q.type || "star" })), { text: newQ.trim(), type: newQType }] });
      setNewQ("");
      setNewQType("star");
      toast.success("Question added — AI is drafting an Urdu translation now");
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to add question"); }
  };

  const archiveQ = async (qid) => {
    try { await api.post(`/admin/cs/templates/${tpl.id}/questions/${qid}/archive`); toast.success("Archived — hidden from the live form, history preserved"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed"); }
  };
  const restoreQ = async (qid) => {
    try { await api.post(`/admin/cs/templates/${tpl.id}/questions/${qid}/restore`); toast.success("Restored — live on the form again"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed"); }
  };
  const deleteQ = async (qid) => {
    try { await api.delete(`/admin/cs/templates/${tpl.id}/questions/${qid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "This question already has responses — it can't be permanently deleted, only archived."); }
  };

  const openUrEditor = (q) => { setUrEditing(q.id); setUrDraft(q.text_ur || ""); };
  const saveUr = async (qid, approve) => {
    if (!urDraft.trim()) return;
    setSavingUr(true);
    try { await api.patch(`/admin/cs/templates/${tpl.id}/questions/${qid}/urdu`, { text_ur: urDraft.trim(), approve }); toast.success(approve ? "Urdu approved" : "Urdu draft saved"); setUrEditing(null); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to save"); } finally { setSavingUr(false); }
  };
  const translateAll = async () => {
    try { await api.post(`/admin/cs/templates/${tpl.id}/translate`); toast.success("AI translation drafted for questions missing Urdu"); load(); }
    catch { toast.error("Translation failed"); }
  };
  const toggleShowUrdu = async (checked) => {
    setTogglingUr(true);
    try { await api.post(`/admin/cs/businesses/${businessId}/toggle-urdu`, { show_urdu_version: checked }, { params: { tid: templateId } }); load(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || "Failed to toggle"); } finally { setTogglingUr(false); }
  };

  return (
    <div id="questions" className="mb-8" data-testid="cs-detail-questions-section">
      <div className="flex items-center justify-between mb-1">
        <div className="overline flex items-center gap-2 text-mango"><Sparkle size={16} /> RATING QUESTIONS</div>
        <Button variant="outline" onClick={translateAll} className="rounded-none text-xs" data-testid="cs-questions-translate-all">AI-translate missing Urdu</Button>
      </div>
      <p className="text-xs text-neutral-500 mb-4">Custom to this business — editing text is always safe. Removing a question that already has responses is only possible via Archive, so historical averages never lose their label.</p>

      <div className="flex items-center justify-between border-2 border-neutral-200 px-3 py-2.5 mb-4">
        <div>
          <Label className="mb-0">Show Urdu on the feedback form</Label>
          <p className="text-xs text-neutral-400">Customers get an En/Ur toggle. Any question without an approved Urdu translation falls back to English automatically.</p>
        </div>
        <Switch checked={!!tpl.show_urdu_version} disabled={togglingUr} onCheckedChange={toggleShowUrdu} data-testid="cs-questions-toggle-urdu" />
      </div>

      <div className="space-y-2 mb-3">
        {active.map((q) => (
          <div key={q.id} className="border-2 border-black p-3" data-testid={`cs-question-${q.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase px-1.5 py-1 shrink-0 border ${q.type === "open_ended" ? "border-mango text-mango" : "border-neutral-400 text-neutral-500"}`} data-testid={`cs-question-type-${q.id}`}>
                {q.type === "open_ended" ? "Open-ended" : "★ Star"}
              </span>
              <Input value={q.text} onChange={(e) => editText(q.id, e.target.value)} onBlur={commitText} className="rounded-none flex-1" data-testid={`cs-question-text-${q.id}`} />
              <button onClick={() => openUrEditor(q)} data-testid={`cs-question-urdu-toggle-${q.id}`} className={`text-xs font-bold px-2 py-1 shrink-0 border ${q.text_ur_status === "approved" ? "border-emerald-600 text-emerald-600" : "border-neutral-300 text-neutral-500"}`}>
                اردو {q.text_ur_status ? `· ${UR_STATUS_LABEL[q.text_ur_status]}` : "· none"}
              </button>
              <button onClick={() => archiveQ(q.id)} data-testid={`cs-question-archive-${q.id}`} className="text-xs font-semibold text-amber-600 hover:text-amber-800 shrink-0 border border-amber-300 px-2 py-1">Archive</button>
              <button onClick={() => deleteQ(q.id)} data-testid={`cs-question-delete-${q.id}`} className="text-xs font-semibold text-red-600 hover:text-red-800 shrink-0 border border-red-300 px-2 py-1">Delete</button>
            </div>
            {urEditing === q.id && (
              <div className="mt-2 pt-2 border-t border-neutral-200" data-testid={`cs-question-urdu-editor-${q.id}`}>
                <Textarea value={urDraft} onChange={(e) => setUrDraft(e.target.value)} dir="rtl" className="rounded-none text-right" placeholder="اردو ترجمہ" data-testid={`cs-question-urdu-input-${q.id}`} />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => saveUr(q.id, true)} disabled={savingUr} className="rounded-none bg-emerald-600 hover:bg-emerald-700" data-testid={`cs-question-urdu-approve-${q.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => saveUr(q.id, false)} disabled={savingUr} className="rounded-none" data-testid={`cs-question-urdu-draft-${q.id}`}>Save as draft</Button>
                  <Button size="sm" variant="ghost" onClick={() => setUrEditing(null)} className="rounded-none">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {active.length === 0 && <div className="text-sm text-neutral-400">No active questions — add one below.</div>}
      </div>

      <div className="flex gap-2 max-w-lg mb-4">
        <Select value={newQType} onValueChange={setNewQType}>
          <SelectTrigger className="rounded-none w-40 shrink-0" data-testid="cs-question-new-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="star">★ Star rating</SelectItem>
            <SelectItem value="open_ended">Open-ended</SelectItem>
          </SelectContent>
        </Select>
        <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Add a new question…" className="rounded-none" data-testid="cs-question-new" onKeyDown={(e) => e.key === "Enter" && addQuestion()} />
        <Button onClick={addQuestion} className="rounded-none bg-black hover:bg-neutral-800 shrink-0" data-testid="cs-question-add"><Plus size={16} className="mr-1" /> Add</Button>
      </div>
      <p className="text-xs text-neutral-400 -mt-3 mb-4">Once a question has any responses, its type (Star / Open-ended) locks — text can still always be edited.</p>

      {archived.length > 0 && (
        <div>
          <button onClick={() => setShowArchived(!showArchived)} data-testid="cs-questions-show-archived" className="text-xs text-neutral-500 hover:text-black underline mb-2">
            {showArchived ? "Hide" : "View"} retired questions ({archived.length})
          </button>
          {showArchived && (
            <div className="space-y-2">
              {archived.map((q) => (
                <div key={q.id} className="border border-dashed border-neutral-300 bg-neutral-50 p-3 flex items-center justify-between gap-2" data-testid={`cs-question-archived-${q.id}`}>
                  <div>
                    <div className="text-sm text-neutral-500">{q.text}</div>
                    <div className="text-[11px] text-neutral-400">RETIRED{q.archived_at && ` on ${formatDate(q.archived_at)}`} — historical responses still shown in Ratings Summary</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => restoreQ(q.id)} data-testid={`cs-question-restore-${q.id}`} className="text-xs font-semibold text-mango">Restore</button>
                    <button onClick={() => deleteQ(q.id)} data-testid={`cs-question-delete-${q.id}`} className="text-xs font-semibold text-red-600">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Business Performance — internal admin-only leaderboard of how every
// Customer Sentiment business is doing (score, volume, trend, billing).
// Not shown on the public directory or the business's own dashboard.
// ============================================================================
export function AdminCSPerformance() {
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    api.get("/admin/cs/businesses/performance").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load performance")).finally(() => setReady(true));
  }, []);
  const fmtDate = (d) => formatDate(d);
  const INVOICE_LABEL = { paid: ["Paid", "text-emerald-600"], pending: ["Pending", "text-amber-600"], none_yet: ["Billed, no invoice yet", "text-neutral-400"], no_billing: ["No billing set", "text-neutral-300"] };
  return (
    <div data-testid="admin-cs-performance-page">
      <Link to="/admin/customer-sentiment" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black mb-4"><ArrowLeft size={14} /> Back to businesses</Link>
      <h1 className="text-2xl font-black tracking-tight mb-2">Business Performance</h1>
      <p className="text-sm text-neutral-500 mb-6">Internal-only leaderboard — how every oodel SCORE business is actually doing. Not visible to businesses or the public.</p>
      <FadeIn ready={ready}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-y-2 border-black text-left">
              <th className="py-3 px-2 overline">Business</th>
              <th className="py-3 px-2 overline">Tier</th>
              <th className="py-3 px-2 overline">Category</th>
              <th className="py-3 px-2 overline text-right">Responses</th>
              <th className="py-3 px-2 overline text-right">Avg ★</th>
              <th className="py-3 px-2 overline text-right">12-mo trend</th>
              <th className="py-3 px-2 overline">Last response</th>
              <th className="py-3 px-2 overline">Payment status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const [label, cls] = INVOICE_LABEL[r.invoice_status] || ["—", "text-neutral-400"];
              return (
                <tr key={r.business_id} className="border-b border-neutral-200" data-testid={`cs-perf-row-${r.business_id}`}>
                  <td className="py-3 px-2 font-bold">
                    <Link to={`/admin/customer-sentiment/${r.business_id}`} className="hover:text-mango">{r.business_name}</Link>
                    {!r.active && <span className="ml-2 text-xs text-red-600 font-normal">(locked)</span>}
                    {r.active && r.billing_suspended && <span className="ml-2 text-xs text-amber-600 font-normal">(suspended — unpaid)</span>}
                  </td>
                  <td className="py-3 px-2 capitalize">{r.tier}</td>
                  <td className="py-3 px-2">{r.category}</td>
                  <td className="py-3 px-2 text-right">{r.total_responses}</td>
                  <td className="py-3 px-2 text-right font-bold">{r.avg_stars || "—"}</td>
                  <td className={`py-3 px-2 text-right font-bold ${r.trend_delta > 0 ? "text-emerald-600" : r.trend_delta < 0 ? "text-red-600" : "text-neutral-400"}`}>{r.trend_delta == null ? "—" : `${r.trend_delta > 0 ? "+" : ""}${r.trend_delta}`}</td>
                  <td className="py-3 px-2 text-neutral-500">{fmtDate(r.last_response_at)}</td>
                  <td className={`py-3 px-2 font-bold ${cls}`}>{label}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-neutral-400">No businesses yet.</td></tr>}
          </tbody>
        </table>
      </FadeIn>
    </div>
  );
}

// ============================================================================
// AI Insight review queue — every freshly-generated oodel X weekly report
// waits here as "pending_review" until an admin edits (optional) and
// approves it. Nothing lands on a business's own dashboard before that.
// ============================================================================
export function AdminCSPendingInsights() {
  const [activeTab, setActiveTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [granularityFilter, setGranularityFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [rebuildStarting, setRebuildStarting] = useState(false);

  const loadPending = () => {
    setRows(null);
    api.get("/admin/cs/ai-insights/pending").then((r) => {
      setRows(r.data);
      const d = {};
      r.data.forEach((row) => { d[row.id] = { summary: row.summary || "", highlights: (row.highlights || []).map((h) => ({ ...h })), show_charts: row.show_charts !== false }; });
      setDrafts(d);
    }).catch(() => toast.error("Failed to load pending insights"));
  };
  const loadHistory = () => {
    setHistory(null);
    api.get("/admin/cs/ai-insights/history").then((r) => setHistory(r.data)).catch(() => toast.error("Failed to load history"));
  };
  useEffect(loadPending, []);
  useEffect(() => { if (activeTab === "history" && history === null) loadHistory(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/admin/cs/ai-insights/rebuild-all/status").then((r) => setRebuildRunning(r.data.running)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!rebuildRunning) return;
    const t = setInterval(() => {
      api.get("/admin/cs/ai-insights/rebuild-all/status").then((r) => {
        setRebuildRunning(r.data.running);
        if (!r.data.running) {
          toast.success("Rebuild complete — every report has been regenerated and is waiting below for approval.");
          loadPending();
          if (activeTab === "history") loadHistory();
        }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [rebuildRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRebuild = async () => {
    setRebuildStarting(true);
    try {
      const r = await api.post("/admin/cs/ai-insights/rebuild-all");
      toast.success(
        r.data.already_running
          ? "Already running — hang tight, this can take a while."
          : "Started — every existing report is being wiped and regenerated for every business/branch. This can take several minutes; check back here."
      );
      setRebuildRunning(true);
      setRebuildConfirmOpen(false);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to start rebuild");
    } finally {
      setRebuildStarting(false);
    }
  };

  const setSummary = (id, v) => setDrafts((p) => ({ ...p, [id]: { ...p[id], summary: v } }));
  const setHighlightLabel = (id, i, v) => setDrafts((p) => {
    const h = [...p[id].highlights];
    h[i] = { ...h[i], label: v };
    return { ...p, [id]: { ...p[id], highlights: h } };
  });

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      const d = drafts[row.id];
      const payload = { summary: d.summary, highlights: d.highlights };
      if (row.branch_ranking?.length) payload.show_charts = d.show_charts;
      await api.put(`/admin/cs/ai-insights/${row.id}`, payload);
      await api.post(`/admin/cs/ai-insights/${row.id}/approve`);
      toast.success(`Approved — now live on ${row.business_name}'s dashboard`);
      setRows((r) => r.filter((x) => x.id !== row.id));
      setHistory(null); // stale — refetch next time History is opened
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/cs/ai-insights/${row.id}/reject`);
      toast.success("Rejected — this report will never show on their dashboard");
      setRows((r) => r.filter((x) => x.id !== row.id));
      setHistory(null);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to reject");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/cs/ai-insights/${deleteTarget.id}`);
      toast.success("Deleted permanently");
      setHistory((h) => (h || []).filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const matchesSearch = (row) => !search.trim() || (row.business_name || "").toLowerCase().includes(search.trim().toLowerCase());
  const matchesGranularity = (row) => granularityFilter === "all" || (row.granularity || "weekly") === granularityFilter;
  const filteredRows = (rows || []).filter((r) => matchesSearch(r) && matchesGranularity(r));
  const filteredHistory = (history || []).filter((r) => matchesSearch(r) && matchesGranularity(r));

  const GranularityBadge = ({ row }) => {
    const g = row.granularity || "weekly";
    const cls = { weekly: "text-neutral-500 bg-neutral-100", monthly: "text-mango bg-amber-50", quarterly: "text-sky-600 bg-sky-50", yearly: "text-violet-600 bg-violet-50" };
    return (
      <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${cls[g] || cls.weekly}`} data-testid={`insight-granularity-badge-${row.id}`}>
        {g.charAt(0).toUpperCase() + g.slice(1)}
      </span>
    );
  };

  return (
    <div data-testid="admin-cs-pending-insights-page">
      <Link to="/admin/customer-sentiment" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black mb-4"><ArrowLeft size={14} /> Back to businesses</Link>
      <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
        <h1 className="text-2xl font-black tracking-tight flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-mango" /> Oodel X Insights — Review Queue</h1>
        <Button
          size="sm" variant="outline" className="rounded-none text-xs shrink-0" disabled={rebuildRunning || rebuildStarting}
          onClick={() => setRebuildConfirmOpen(true)} data-testid="cs-rebuild-all-btn"
        >
          {rebuildRunning ? "Rebuilding every report…" : "Rebuild All Reports (one-time)"}
        </Button>
      </div>
      <p className="text-sm text-neutral-500 mb-4">Every branch's weekly report, and every month/quarter/year's combined whole-business report, lands here first — fully automatic, no minimum response count. Edit the narrative or highlights if needed, then approve to publish it on the business's own dashboard — or reject to discard it for good.</p>


      <div className="flex items-center justify-between flex-wrap gap-3 mb-5 border-b border-neutral-200">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-3 py-2 text-sm font-bold ${activeTab === "pending" ? "border-b-2 border-black" : "text-neutral-400"}`}
            data-testid="cs-insights-tab-pending"
          >
            Pending {rows ? `(${rows.length})` : ""}
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-3 py-2 text-sm font-bold ${activeTab === "history" ? "border-b-2 border-black" : "text-neutral-400"}`}
            data-testid="cs-insights-tab-history"
          >
            History
          </button>
        </div>
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex items-center border border-neutral-200 rounded-lg overflow-hidden text-xs font-semibold" data-testid="cs-insights-granularity-filter">
            {["all", "weekly", "monthly", "quarterly", "yearly"].map((g) => (
              <button
                key={g} onClick={() => setGranularityFilter(g)}
                className={`px-2.5 py-1.5 capitalize ${granularityFilter === g ? "bg-black text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}
                data-testid={`cs-insights-granularity-filter-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by business name…"
              className="rounded-none pl-8 h-9 text-sm w-64" data-testid="cs-insights-search"
            />
          </div>
        </div>
      </div>

      {activeTab === "pending" && (
        <>
          {rows === null && <div className="text-sm text-neutral-400">Loading…</div>}
          {rows && filteredRows.length === 0 && (
            <div className="text-sm text-neutral-400 border border-dashed border-neutral-200 rounded-lg p-8 text-center">
              {rows.length === 0 ? "Nothing waiting for review right now." : "No pending insights match that filter."}
            </div>
          )}
          <div className="space-y-5">
            {filteredRows.map((row) => (
              <div key={row.id} className="bg-white border-2 border-black p-5" data-testid={`pending-insight-${row.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <div className="font-bold text-lg flex items-center gap-2">
                      {row.business_name}{row.location_name ? ` — ${row.location_name}` : ""}
                      <GranularityBadge row={row} />
                    </div>
                    <div className="text-xs text-neutral-400">{formatRangeInclusive(row.period_start, row.period_end)} · generated {formatDate(row.generated_at)}</div>
                  </div>
                </div>

                {(row.granularity || "weekly") === "weekly" && (
                  <div className="flex items-center gap-4 text-xs text-neutral-500 mb-3" data-testid={`pending-insight-stats-${row.id}`}>
                    <span><strong className="text-black">{row.responses ?? 0}</strong> responses</span>
                    <span><strong className="text-black">{row.avg_score != null ? `${row.avg_score}/5` : "—"}</strong> avg score</span>
                    <span><strong className="text-black">{row.scans ?? 0}</strong> scans</span>
                  </div>
                )}

                {row.highlights?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {(drafts[row.id]?.highlights || []).map((h, i) => (
                      <Input
                        key={i} value={h.label} onChange={(e) => setHighlightLabel(row.id, i, e.target.value)}
                        className="rounded-none text-xs" data-testid={`pending-insight-highlight-${row.id}-${i}`}
                      />
                    ))}
                  </div>
                )}

                <Label className="overline">Narrative</Label>
                <Textarea
                  value={drafts[row.id]?.summary || ""} onChange={(e) => setSummary(row.id, e.target.value)}
                  className="rounded-none min-h-[120px] mb-4" data-testid={`pending-insight-summary-${row.id}`}
                />

                {row.watch_flags?.length > 0 && (
                  <div className="mb-4 space-y-1">
                    {row.watch_flags.map((f, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5"><WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" /> {f}</div>
                    ))}
                  </div>
                )}

                {row.branch_ranking?.length > 0 && (
                  <div className="mb-4 border border-neutral-200 rounded-lg p-3" data-testid={`pending-insight-branch-ranking-${row.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="overline text-neutral-500">Branch performance this month</div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-600 cursor-pointer">
                        Show chart to business
                        <Switch
                          checked={drafts[row.id]?.show_charts !== false}
                          onCheckedChange={(v) => setDrafts((p) => ({ ...p, [row.id]: { ...p[row.id], show_charts: v } }))}
                          data-testid={`pending-insight-show-charts-${row.id}`}
                        />
                      </label>
                    </div>
                    <div className="space-y-1">
                      {row.branch_ranking.map((b, i) => (
                        <div key={b.location_id} className="flex items-center gap-2 text-xs">
                          <span className="w-5 text-neutral-400">{i + 1}.</span>
                          <span className="flex-1 font-semibold">{b.location_name}</span>
                          <span className="text-neutral-500">{b.responses} resp.</span>
                          <span className="font-bold w-10 text-right">{b.avg_score}/5</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Button onClick={() => approve(row)} disabled={busyId === row.id} className="rounded-none bg-black hover:bg-neutral-800" data-testid={`pending-insight-approve-${row.id}`}>
                    <CheckCircle size={15} className="mr-1.5" /> Approve &amp; publish
                  </Button>
                  <Button onClick={() => reject(row)} disabled={busyId === row.id} variant="outline" className="rounded-none" data-testid={`pending-insight-reject-${row.id}`}>
                    <Trash size={15} className="mr-1.5" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === "history" && (
        <>
          {history === null && <div className="text-sm text-neutral-400">Loading…</div>}
          {history && filteredHistory.length === 0 && (
            <div className="text-sm text-neutral-400 border border-dashed border-neutral-200 rounded-lg p-8 text-center">
              {history.length === 0 ? "No reviewed insights yet." : "No history matches that filter."}
            </div>
          )}
          <div className="space-y-3">
            {filteredHistory.map((row) => (
              <div key={row.id} className="bg-white border border-neutral-200 p-4" data-testid={`history-insight-${row.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                  <div className="font-bold flex items-center gap-2">
                    {row.business_name}{row.location_name ? ` — ${row.location_name}` : ""}
                    <GranularityBadge row={row} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${row.status === "approved" ? "text-emerald-700 bg-emerald-50" : "text-neutral-400 bg-neutral-100"}`}
                      data-testid={`history-status-${row.id}`}
                    >
                      {row.status}
                    </span>
                    <button onClick={() => setDeleteTarget(row)} className="text-neutral-400 hover:text-red-600" data-testid={`history-delete-${row.id}`} title="Delete permanently">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-neutral-400 mb-2">
                  {formatRangeInclusive(row.period_start, row.period_end)} · reviewed {formatDate(row.reviewed_at)} by {row.reviewer_name || "Auto (no reviewer needed)"}
                </div>
                {row.summary && <p className="text-sm text-neutral-600 leading-snug">{row.summary}</p>}
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-none" data-testid="delete-insight-dialog">
          <DialogHeader>
            <DialogTitle>Delete this insight permanently?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500">
            This removes {deleteTarget?.business_name}{deleteTarget?.location_name ? ` — ${deleteTarget.location_name}` : ""}'s
            {" "}{formatRangeInclusive(deleteTarget?.period_start, deleteTarget?.period_end)} report entirely — it will no
            longer appear in History, and this cannot be undone. It won't affect any other week's or month's data.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDeleteTarget(null)} data-testid="delete-insight-cancel">Cancel</Button>
            <Button className="rounded-none bg-red-600 hover:bg-red-700" disabled={deleting} onClick={confirmDelete} data-testid="delete-insight-confirm">
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rebuildConfirmOpen} onOpenChange={(o) => !o && setRebuildConfirmOpen(false)}>
        <DialogContent className="rounded-none" data-testid="rebuild-all-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Rebuild every Oodel X report?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500">
            This permanently deletes EVERY existing weekly, monthly, quarterly and yearly report — for every business
            and every branch, including ones already approved and live on a business's dashboard — and regenerates
            all of them from scratch. Every regenerated report lands back here for approval, so businesses will see
            a blank Oodel X page until you re-approve each one. Meant to be run once, right after deploying a
            change like this. It can take several minutes for businesses with a lot of history.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setRebuildConfirmOpen(false)} data-testid="rebuild-all-cancel">Cancel</Button>
            <Button className="rounded-none bg-red-600 hover:bg-red-700" disabled={rebuildStarting} onClick={startRebuild} data-testid="rebuild-all-confirm">
              {rebuildStarting ? "Starting…" : "Delete & rebuild everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Templates
// ============================================================================
export function AdminCSTemplates() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("all");

  useEffect(() => { api.get("/admin/cs/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const load = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (category !== "all") params.category = category;
    if (city !== "all") params.city = city;
    api.get("/admin/cs/templates", { params }).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load templates")).finally(() => setReady(true));
  };
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [q, category, city]);

  const allCities = [...new Set(rows.flatMap((t) => t.cities || []))].sort();

  return (
    <div data-testid="admin-cs-templates-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black tracking-tight mb-1">Business Templates</h1>
        <p className="text-sm text-neutral-500">Every business owns its own set of rating questions, tagged to a category. Click through to a business to edit its questions, archive old ones, or manage Urdu translations.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by business name…" className="rounded-none pl-9" data-testid="cs-tpl-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="rounded-none w-[180px]" data-testid="cs-tpl-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="rounded-none w-[160px]" data-testid="cs-tpl-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {allCities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <FadeIn ready={ready}>
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((t) => (
            <Link to={`/admin/customer-sentiment/${t.business_id}`} key={t.id} className="border-2 border-black p-4 block hover:bg-neutral-50" data-testid={`cs-tpl-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold">{t.business_name}</div>
                  <div className="text-xs text-neutral-400 flex items-center gap-2 mt-0.5">
                    {t.category && <span>{t.category}</span>}
                    {t.cities?.length > 0 && <span className="inline-flex items-center gap-0.5"><MapPin size={11} />{t.cities.join(", ")}</span>}
                  </div>
                </div>
                <span className="text-xs font-bold text-neutral-500 shrink-0">{t.active_question_count} question{t.active_question_count === 1 ? "" : "s"}</span>
              </div>
              <ol className="text-sm list-decimal list-inside text-neutral-600 space-y-0.5">
                {t.questions.filter((q2) => q2.active !== false).map((q2) => <li key={q2.id}>{q2.text}</li>)}
              </ol>
            </Link>
          ))}
          {rows.length === 0 && <div className="col-span-2 text-center text-neutral-400 py-12">No businesses match those filters.</div>}
        </div>
      </FadeIn>
    </div>
  );
}
